/**
 * Hook for managing inline row editing state in WarehouseTable.
 *
 * Encapsulates the editing state (which row, edited values, saving state)
 * and provides callback functions. Returns an InlineEditConfig<T> object
 * that can be passed directly to WarehouseTable's inlineEditConfig prop.
 *
 * Usage:
 * ```typescript
 * const inlineEditConfig = useInlineEdit<MyDto>({
 *   getRowId: (row) => row.id,
 *   onSave: async (originalRow, editedData) => {
 *     await myService.update(originalRow.id, editedData);
 *   },
 *   onSaveSuccess: () => loadData()
 * });
 *
 * <WarehouseTable inlineEditConfig={inlineEditConfig} ... />
 * ```
 */

import { useState, useCallback } from 'react';
import type { TableDataRow, InlineEditConfig } from '../types/table';

export interface UseInlineEditOptions<T extends TableDataRow> {
  /** Extract the unique ID from a row */
  getRowId: (row: T) => string | number;
  /** Called when saving edited data. Throw to indicate failure. */
  onSave: (originalRow: T, editedData: Partial<T>) => Promise<void>;
  /** Called after successful save (e.g., reload data) */
  onSaveSuccess?: () => void;
  /** Called when save fails */
  onSaveError?: (error: any) => void;
}

export function useInlineEdit<T extends TableDataRow>(
  options: UseInlineEditOptions<T>
): InlineEditConfig<T> {
  const [editingRowId, setEditingRowId] = useState<string | number | null>(null);
  const [editingData, setEditingData] = useState<Partial<T>>({});
  const [isSaving, setIsSaving] = useState(false);

  const onEditStart = useCallback((row: T) => {
    setEditingRowId(options.getRowId(row));
    setEditingData({ ...row });
  }, [options.getRowId]);

  const onEditCancel = useCallback(() => {
    setEditingRowId(null);
    setEditingData({});
  }, []);

  const onEditSave = useCallback(async (row: T, data: Partial<T>) => {
    setIsSaving(true);
    try {
      await options.onSave(row, data);
      setEditingRowId(null);
      setEditingData({});
      options.onSaveSuccess?.();
    } catch (err: any) {
      options.onSaveError?.(err);
    } finally {
      setIsSaving(false);
    }
  }, [options.onSave, options.onSaveSuccess, options.onSaveError]);

  const onEditFieldChange = useCallback((field: keyof T, value: any) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    editingRowId,
    editingData,
    onEditStart,
    onEditSave,
    onEditCancel,
    onEditFieldChange,
    isSaving
  };
}
